import {
  EndReopenBracketResponse,
  GetBracketDetailResponse,
  GetBracketMatchResponse,
  GetBracketPaymentsResponse,
  GetBracketPlayersResponse,
  GetBracketQueuesResponse,
  GetBracketStandingsResponse,
  GetBracketSwitchPlayersResponse,
  GetBracketSwitchTeamsResponse,
  GetBracketTeamsResponse,
  GetBracketWaitListTeamsResponse,
  GetBracketsValidResponse,
  GetEventAnnouncementsResponse,
  GetEventDetailResponse,
  GetEventsResponse,
  ParamsGetBracketMatch,
  PayloadBracketAddParticipants,
  PayloadBracketCreateTeam,
  PayloadBracketEditTeam,
  PayloadEditPartner,
  PayloadGetBracketPayments,
  PayloadGetBracketPlayers,
  PayloadGetBracketTeams,
  PayloadGetBracketUnmatchedPlayers,
  PayloadGetBracketWaiListTeams,
  PayloadGetEventAnnouncements,
  PayloadGetEvents,
  PayloadGetMyEvents,
  PayloadReopenBracket,
  PayloadSaveAnnouncement,
  PayloadSaveEvent,
  PayloadSwitchWaitList,
  PayloadWithdrawPlayer,
  PublishSeedMatchesPayload,
  Response,
  SeedMatchesParams,
  SeedMatchesResponse,
  Status,
  UpdatePartnerResponse,
} from '@/types'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import toast from 'react-hot-toast'
import httpService from './httpService'

const eventPath = '/event/v1.0'
const bracketPath = '/brackets/v1.0'
const bracketMatchPath = '/brackets/match/v1.0'
const bracketPlayersPath = '/brackets/participant/v1.0'
const createAnnouncementPath = `event/announcement/v1.0/save`
const editAnnouncementPath = `event/announcement/v1.0/edit`
const saveEventPath = `${eventPath}/save`
const draftEventPath = `${eventPath}/draft`
const bracketQueuesPath = '/brackets'
const bracketRemoveQueuePath = '/brackets/match/remove'
const bracketPendingConfirmPath = '/brackets/teams/v1.0/pending'
const bracketUnmatchedPlayersPath = '/brackets/participant/unmatched/v1.0/all'
const bracketPaymentsPath = '/event/director/participant/v1.0/all'
const bracketStandingsPath = `${bracketPath}/standing`
const bracketWaitListTeamPath = `${bracketPath}/waitlist`
const bracketEditTeamPath = '/brackets/director'
const bracketTeamsPath = `${bracketPath}/teams`
const bracketSwitchTeamsPath = '/brackets/director/team/v1.0/switch'
const bracketSwitchPlayersPath = '/brackets/director/player/v1.0/switch'
const bracketGetPlayerRolePath = '/brackets/club/roles/v1.0/details'
const bracketConfirmTeamRolePath = '/brackets/director/teams/v1.0/confirm'
const bracketAddParticipantsPath = '/brackets/director/user/v1.0/add'
const bracketRefundPath = '/brackets/director/v1.0/refund'
const bracketWithdrawPath = '/brackets/director/v1.0/withdraw'
const bracketPostSubstitute = '/brackets/director/participant/v1.0/substitute'
const bracketEditConfirmMatch = '/brackets/director/confirm/match/v1.0/edit'
const bracketDirectorEditScores = '/brackets/director/match/v1.0/edit'
const bracketDirectorAddScores = '/brackets/director/match/v1.0/save'
const bracketPlayerEditScores = '/brackets/match/v1.0/edit'
const bracketPlayerAddScores = '/brackets/match/v1.0/save'
const bracketForfeitBracketMatch = '/brackets/director/match/v1.0/forfeit'
const getEventAnnouncementPath = 'event/announcement/v1.0/all'
const bracketsValidPath = '/brackets/valid/v1.0'
const bracketsApprovalPath = '/brackets/approval/v1.0/update'
const bracketsPlayerWithdrawPath = '/brackets/player/v1.0/withdraw'
const bracketsPartnerEditPath = '/brackets/partner/v1.0/edit'
const bracketEditStatusPath = '/brackets/director/v1.0/edit/bracket_status'
const bracketEditPath = '/brackets/v1.0/edit'
const bracketsSeedPath = '/brackets/v1.0/seed'
const eventEditPath = '/event/v1.1/edit'
const bracketSavePath = '/brackets/v1.0/save'
const bracketSwitchWaitListedPath = '/brackets/director/team/v1.0/switchWaitlisted'

enum EventQueryKeys {
  GET_BRACKET_PLAYERS = 'get_bracket_players',
  GET_BRACKET_PAYMENTS = 'get_bracket_payments',
  GET_EVENTS = 'get_events',
  GET_MY_EVENTS = 'get_my_events',
}

export const getMyEvents = async (payload: PayloadGetMyEvents) => {
  return await httpService.Post<GetEventsResponse>(`${eventPath}/all`, payload)
}

export const getEvents = async (payload: PayloadGetEvents) => {
  return await httpService.Post<GetEventsResponse>(`${eventPath}/search`, payload)
}

export const getEventDetail = async (eventId: string) => {
  return await httpService.Get<GetEventDetailResponse>(`${eventPath}/${eventId}`)
}

export const getBracketDetail = async (bracketId: string) => {
  return await httpService.Get<GetBracketDetailResponse>(`${bracketPath}/${bracketId}`)
}

export const useGetBracketMatches = () => {
  return useMutation([bracketMatchPath], async (params: ParamsGetBracketMatch) => {
    const res = await httpService.Post(
      `${bracketMatchPath}/history?bracketId=${params.bracketId}&round=${params.round}&limit=${params.limit}&offset=${params.offset}`,
      {
        ...(params.tags.length && {
          tags: params.tags,
        }),
      }
    )
    return res as GetBracketMatchResponse
  })
}

export const useGetBracketPlayers = (payload: PayloadGetBracketPlayers, userId?: number) => {
  return useQuery(
    [EventQueryKeys.GET_BRACKET_PLAYERS, JSON.stringify(payload), userId],
    async ({ signal }) => {
      const { limit, offset, ...restPayload } = payload
      const res = await httpService.Post(`${bracketPlayersPath}/all?limit=${limit}&offset=${offset}`, restPayload, {
        signal,
      })
      return res as GetBracketPlayersResponse
    },
    {
      enabled: !!userId,
      cacheTime: 0,
    }
  )
}

export const useGetBracketUnmatchedPlayers = () => {
  return useMutation([], async (payload: PayloadGetBracketUnmatchedPlayers) => {
    const res = await httpService.Post(bracketUnmatchedPlayersPath, payload)
    return res as GetBracketPlayersResponse
  })
}

export const useGetBracketPayments = (payload: PayloadGetBracketPayments, token: string) => {
  return useQuery(
    [EventQueryKeys.GET_BRACKET_PAYMENTS, JSON.stringify(payload), token],
    async ({ signal }) => {
      const { limit, offset, ...restPayload } = payload
      const res = await httpService.Post(`${bracketPaymentsPath}?limit=${limit}&offset=${offset}`, restPayload, {
        signal,
      })
      return res as GetBracketPaymentsResponse
    },
    {
      enabled: !!token,
      cacheTime: 0,
    }
  )
}

export const useGetBracketStandings = (bracketId?: string, limit?: number, offset?: number, round?: number) => {
  return useQuery(
    [eventPath, bracketId, limit, offset],
    async () => {
      return await httpService.Get<GetBracketStandingsResponse>(
        `${bracketStandingsPath}?bracketId=${bracketId}&limit=${limit}&offset=${offset}&round=${round}`
      )
    },
    {
      enabled: !!bracketId,
      cacheTime: 0,
      refetchOnWindowFocus: false,
    }
  )
}

export const useGetBracketTeams = () => {
  return useMutation([], async (payload: PayloadGetBracketTeams) => {
    const { format, ...restPayload } = payload
    const res = await httpService.Post(`${bracketTeamsPath}?format=${format}`, restPayload)
    return res as GetBracketTeamsResponse
  })
}

export const useBracketSwitchTeams = () => {
  return useMutation([], async (payload: any) => {
    const res = await httpService.Post(bracketSwitchTeamsPath, payload)
    return res as GetBracketSwitchTeamsResponse
  })
}

export const useBracketSwitchPlayers = () => {
  return useMutation([], async (payload: any) => {
    const res = await httpService.Post(bracketSwitchPlayersPath, payload)
    return res as GetBracketSwitchPlayersResponse
  })
}

export const useBracketCreateTeam = () => {
  return useMutation([], async (payload: PayloadBracketCreateTeam) => {
    const res = await httpService.Post(`/brackets/director/${payload.bracketId}/teams/v1.0/save`, payload)
    return res as any
  })
}

export const useGetBracketWaitListTeams = () => {
  return useMutation([], async (payload: PayloadGetBracketWaiListTeams) => {
    const { format, ...restPayload } = payload
    const res = await httpService.Post(`${bracketWaitListTeamPath}/teams?format=${format}`, restPayload)
    return res as GetBracketWaitListTeamsResponse
  })
}

export const useSaveEvent = () => {
  return useMutation([saveEventPath], async (payload: PayloadSaveEvent) => {
    const res = await httpService.Post(`${saveEventPath}`, payload)
    return res as any
  })
}

export const useCreateAnnouncement = () => {
  return useMutation([createAnnouncementPath], async (payload: PayloadSaveAnnouncement) => {
    const res = await httpService.Post(`${createAnnouncementPath}`, payload)
    return res as any
  })
}

export const useEditAnnouncement = () => {
  return useMutation([editAnnouncementPath], async (payload: PayloadSaveAnnouncement) => {
    const res = await httpService.Post(`${editAnnouncementPath}`, payload)
    return res as any
  })
}

export const useRemoveEvent = () => {
  return useMutation([], async (eventId: number) => {
    const res = await httpService.Delete(`${eventPath}/${eventId}`)
    return res as Response
  })
}

export const useDraftEvent = () => {
  return useMutation([draftEventPath], async (payload: PayloadSaveEvent) => {
    const res = await httpService.Post(`${draftEventPath}`, payload)
    return res as any
  })
}

export const useGetEvent = (eventId?: string) => {
  return useQuery(
    [eventPath, eventId],
    async () => {
      return await httpService.Get<GetEventDetailResponse>(`${eventPath}/${eventId}`)
    },
    {
      enabled: !!eventId,
    }
  )
}

export const useGetBracketQueues = (bracketId?: number, limit?: number, offset?: number) => {
  return useQuery(
    [bracketQueuesPath, bracketId, limit, offset],
    async () => {
      return await httpService.Get<GetBracketQueuesResponse>(
        `${bracketQueuesPath}/${bracketId}/queue/v1.0/all?limit=${limit}&offset=${offset}`
      )
    },
    {
      enabled: !!bracketId,
    }
  )
}

export const useRemoveBracketQueue = (leagueMatchId: number | null, confirmDelete: boolean) => {
  const queryClient = useQueryClient()
  return useQuery(
    [bracketRemoveQueuePath, leagueMatchId],
    async () => {
      return await httpService.Get<any>(`/brackets/match/${leagueMatchId}/queue/v1.0/remove`)
    },
    {
      onSuccess: () => {
        queryClient.invalidateQueries([bracketQueuesPath])
      },
      enabled: confirmDelete,
    }
  )
}

export const useGetBracketPendingConfirmation = (bracketId?: number, userId?: number) => {
  return useQuery(
    [bracketPendingConfirmPath, bracketId],
    async () => {
      return await httpService.Get<any>(`${bracketPendingConfirmPath}?bracketId=${bracketId}`)
    },
    {
      enabled: !!bracketId && !!userId,
    }
  )
}

export const useBracketEditTeam = () => {
  return useMutation([], async (payload: PayloadBracketEditTeam) => {
    const { bracketId } = payload
    const res = await httpService.Post(`${bracketEditTeamPath}/${bracketId}/teams/v1.0/edit`, payload)
    return res as { message: string; status: Status }
  })
}

export const useGetPlayRole = () => {
  return useMutation([], async (payload: { bracketId: string; userId: number }) => {
    const res = await httpService.Post(bracketGetPlayerRolePath, payload)
    return res as {
      result: {
        roleId: null
        isClubMember: false
        roleName: null
      }
      status: Status
    }
  })
}

export const useBracketAddParticipants = () => {
  return useMutation([], async (payload: PayloadBracketAddParticipants) => {
    const res = await httpService.Post(bracketAddParticipantsPath, payload)
    return res as any
  })
}

export const useBracketConfirmTeam = () => {
  return useMutation([], async (payload: { bracketId: string; registrationId: number }) => {
    const res = await httpService.Post(bracketConfirmTeamRolePath, payload)
    return res as {
      message: string
      status: Status
    }
  })
}

export const useGetPlayerRegisteredBrackets = (clubId?: string, currentPlayerId?: number) => {
  return useQuery(
    [clubId, currentPlayerId],
    async () => {
      return await httpService.Get<any>(`/brackets/director/${clubId}/${currentPlayerId}/v1.0/all`)
    },
    {
      enabled: !!clubId && !!currentPlayerId,
    }
  )
}

export const useBracketRefund = () => {
  return useMutation([], async (payload: any) => {
    const res = await httpService.Post(bracketRefundPath, payload)
    return res as any
  })
}

export const useBracketWithdraw = () => {
  return useMutation([], async (payload: any) => {
    const res = await httpService.Post(bracketWithdrawPath, payload)
    return res as any
  })
}

export const usePostSubstitutePlayers = () => {
  return useMutation(
    [],
    async (payload: any) => {
      const res = await httpService.Post(bracketPostSubstitute, payload)
      return res as any
    },
    {
      onError: (error: any) => {
        toast.error(error?.response?.data?.message)
      },
    }
  )
}

export const useEditConfirmMatch = () => {
  return useMutation(
    [],
    async (payload: any) => {
      const res = await httpService.Post(bracketEditConfirmMatch, payload)
      return res as any
    },
    {
      onError: (error: any) => {
        toast.error(error?.response?.data?.message)
      },
    }
  )
}

export const useDirectorEditScores = () => {
  return useMutation(
    [],
    async (payload: any) => {
      const res = await httpService.Post(bracketDirectorEditScores, payload)
      return res as any
    },
    {
      onError: (error: any) => {
        toast.error(error?.response?.data?.message)
      },
    }
  )
}

export const usePlayerEditScores = () => {
  return useMutation(
    [],
    async (payload: any) => {
      const res = await httpService.Post(bracketPlayerEditScores, payload)
      return res as any
    },
    {
      onError: (error: any) => {
        toast.error(error?.response?.data?.message)
      },
    }
  )
}

export const useForfeitBracketMatch = () => {
  return useMutation(
    [],
    async (payload: any) => {
      const res = await httpService.Post(bracketForfeitBracketMatch, payload)
      return res as any
    },
    {
      onError: (error: any) => {
        toast.error(error?.response?.data?.message)
      },
    }
  )
}

export const useDirectorAddScores = () => {
  return useMutation(
    [],
    async (payload: any) => {
      const res = await httpService.Post(bracketDirectorAddScores, payload)
      return res as any
    },
    {
      onError: (error: any) => {
        toast.error(error?.response?.data?.message)
      },
    }
  )
}

export const usePlayerAddScores = () => {
  return useMutation(
    [],
    async (payload: any) => {
      const res = await httpService.Post(bracketPlayerAddScores, payload)
      return res as any
    },
    {
      onError: (error: any) => {
        toast.error(error?.response?.data?.message)
      },
    }
  )
}

export const useGetEventAnnouncements = () => {
  return useMutation([], async (payload: PayloadGetEventAnnouncements) => {
    const res = await httpService.Post(getEventAnnouncementPath, payload)
    return res as GetEventAnnouncementsResponse
  })
}

export const useGetBracketsValid = (eventId?: string) => {
  return useQuery(
    [bracketsValidPath, eventId],
    async () => {
      const res = await httpService.Get<GetBracketsValidResponse>(`${bracketsValidPath}/${eventId}`)
      return res.results
    },
    {
      enabled: !!eventId,
      staleTime: 5 * 60 * 1000,
    }
  )
}

export const useUpdatePartner = (bracketId: string, isClubMember: boolean, registrationId: string, status: string) => {
  return useQuery(
    [bracketsApprovalPath, bracketId, registrationId, status, isClubMember],
    async () => {
      const res =
        await httpService.Get<UpdatePartnerResponse>(`${bracketsApprovalPath}/?bracketId=${bracketId}&isClubMember=${isClubMember}&registrationId=${registrationId}&status=${status}
      `)
      return res
    },
    {
      enabled: !!bracketId && !!registrationId && !!status,
      staleTime: 5 * 60 * 1000,
    }
  )
}

export const useWithdrawPlayer = () => {
  return useMutation([], async (payload: PayloadWithdrawPlayer) => {
    const res = await httpService.Post(bracketsPlayerWithdrawPath, payload)
    return res as any
  })
}

export const useEditPartner = () => {
  return useMutation([], async (payload: PayloadEditPartner) => {
    const res = await httpService.Post(bracketsPartnerEditPath, payload)
    return res as any
  })
}

export const useEndBracket = (eventId: string, bracketId: string) => {
  return useQuery(
    [eventId, bracketId],
    async () => {
      return await httpService.Get<EndReopenBracketResponse>(`/brackets/${eventId}/${bracketId}/v1.0/end`)
    },
    {
      enabled: !!eventId && !!bracketId,
      staleTime: 5 * 60 * 1000,
    }
  )
}

export const useReopenBracket = () => {
  return useMutation([], async (payload: PayloadReopenBracket) => {
    const res = await httpService.Put(
      `${bracketEditStatusPath}?bracketId=${payload.bracketId}&clubId=${payload.clubId}&leagueId=${payload.leagueId}&status=${payload.status}`,
      {}
    )
    return res as any
  })
}

export const useEditBracket = () => {
  return useMutation([], async (payload: any) => {
    const res = await httpService.Put(bracketEditPath, payload)
    return res as any
  })
}

export const useSeedMatches = () => {
  return useMutation([], async (params: SeedMatchesParams) => {
    const res = await httpService.Get<SeedMatchesResponse>(
      `${bracketsSeedPath}?bracketId=${params.bracketId}&type=${params.type}`
    )
    return res.results
  })
}

export const usePublishSeedMatches = () => {
  return useMutation([], async (payload: PublishSeedMatchesPayload) => {
    const { bracketId, seedMatches } = payload
    const res = await httpService.Post(`${bracketsSeedPath}?bracketId=${bracketId}`, seedMatches)
    return res as any
  })
}

export const useGetAllSeedMatches = (bracketId?: string) => {
  return useQuery(
    [bracketId],
    async () => {
      return await httpService.Get<any>(`brackets/${bracketId}/match/v1.0/all`)
    },
    {
      enabled: !!bracketId,
    }
  )
}

export const useEditEvent = () => {
  return useMutation([], async (payload: any) => {
    const res = await httpService.Put(eventEditPath, payload)
    return res as any
  })
}

export const useEndEvent = () => {
  return useMutation([], async (eventId: string) => {
    const res = await httpService.Get<any>(`event/${eventId}/v1.0/end`)
    return res.results
  })
}

export const useSaveBracket = () => {
  return useMutation([], async (payload: any) => {
    const res = await httpService.Put(bracketSavePath, payload)
    return res as any
  })
}

export const useGetBracketMatchStructure = (bracketId?: string) => {
  return useQuery(
    [bracketId],
    async () => {
      return await httpService.Get<any>(`brackets/match/${bracketId}/v1.0/structure`)
    },
    {
      enabled: !!bracketId,
      refetchOnWindowFocus: false,
    }
  )
}

export const useGetMyEvents = () => {
  return useMutation([], async (payload: PayloadGetMyEvents) => {
    const res = await httpService.Post(`${eventPath}/all`, payload)
    return res as GetEventsResponse
  })
}

export const useGetEvents = () => {
  return useMutation([], async (payload: PayloadGetEvents) => {
    const res = await httpService.Post(`${eventPath}/search`, payload)
    return res as GetEventsResponse
  })
}

export const useSwitchWaitListed = () => {
  return useMutation([], async (payload: PayloadSwitchWaitList) => {
    const res = await httpService.Post(bracketSwitchWaitListedPath, payload)
    return res as any
  })
}

export const useQueryGetEvents = (payload: PayloadGetEvents, enabled = true) => {
  return useQuery({
    queryKey: [EventQueryKeys.GET_EVENTS, JSON.stringify(payload)],
    queryFn: ({ signal }) => {
      return httpService.Post<GetEventsResponse>(`${eventPath}/search`, payload, { signal })
    },
    refetchOnWindowFocus: false,
    cacheTime: 0,
    enabled: enabled,
  })
}

export const useQueryGetMyEvents = (payload: PayloadGetMyEvents, enabled = true) => {
  return useQuery({
    queryKey: [EventQueryKeys.GET_MY_EVENTS, JSON.stringify(payload)],
    queryFn: () => {
      return httpService.Post<GetEventsResponse>(`${eventPath}/all`, payload)
    },
    refetchOnWindowFocus: false,
    cacheTime: 0,
    enabled: enabled,
  })
}
